import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Table, Spinner, ProgressBar } from 'react-bootstrap';
import { useAuth } from '../../hooks/useAuth';
import { 
  FaClipboardList, 
  FaFileInvoice, 
  FaCheckCircle, 
  FaExclamationCircle,
  FaArrowUp,
  FaArrowDown,
  FaUsers
} from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BackofficeHome = () => {
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Backoffice Home';
  }, []);

  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Lead Processing Metrics',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Chart data
  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Leads Received',
        data: [65, 78, 86, 94, 80, 92],
        backgroundColor: 'rgba(99, 102, 241, 0.5)',
      },
      {
        label: 'Processed Leads',
        data: [54, 67, 73, 80, 71, 85],
        backgroundColor: 'rgba(56, 178, 172, 0.5)',
      },
      {
        label: 'Approved Leads',
        data: [32, 41, 45, 52, 48, 56],
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
      },
    ],
  };

  // Mock recent leads
  const recentLeads = [
    { id: 1, name: "Amit Kumar", product: "Personal Loan", status: "New", date: "2025-04-20", completion: 85 },
    { id: 2, name: "Priya Sharma", product: "Credit Card", status: "Processing", date: "2025-04-19", completion: 60 },
    { id: 3, name: "Rajesh Singh", product: "Home Loan", status: "Completed", date: "2025-04-18", completion: 100 },
    { id: 4, name: "Sunita Patel", product: "Personal Loan", status: "Rejected", date: "2025-04-17", completion: 40 },
    { id: 5, name: "Deepak Verma", product: "Car Loan", status: "Processing", date: "2025-04-16", completion: 75 },
  ];

  // Mock lead stats
  const leadStats = {
    totalLeads: 156,
    totalChange: '+16%',
    newLeads: 42,
    newChange: '+24%',
    processedLeads: 89,
    processedChange: '+18%',
    completedLeads: 25,
    completedChange: '-8%',
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    let variant = "primary";
    
    switch(status) {
      case "New":
        variant = "info";
        break;
      case "Processing":
        variant = "warning";
        break;
      case "Completed":
        variant = "success";
        break;
      case "Rejected":
        variant = "danger";
        break;
      default:
        variant = "primary";
    }
    
    return <Badge bg={variant}>{status}</Badge>;
  };

  // Render progress bar
  const renderProgressBar = (completion) => {
    let variant = "primary";
    
    if (completion < 30) {
      variant = "danger";
    } else if (completion < 70) {
      variant = "warning";
    } else {
      variant = "success";
    }
    
    return (
      <ProgressBar 
        now={completion} 
        variant={variant} 
        label={`${completion}%`}
        style={{ height: '10px', fontSize: '8px' }}
      />
    );
  };

  return (
    <div className="backoffice-home-container">
      <div className="backoffice-dashboard-welcome">
        <h1>Welcome, {currentUser?.name || 'Backoffice'}</h1>
        <p>Manage and process leads from your dashboard</p>
      </div>
      
      <Row className="g-3 mb-4">
        {/* Lead Stats Cards */}
        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(99, 102, 241, 0.1)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaClipboardList size={24} color="#6366f1" />
              </div>
              <div>
                <div className="text-muted small mb-1">Total Leads</div>
                <h3 className="mb-1 fs-4">{leadStats.totalLeads}</h3>
                <div className="small text-success">
                  <FaArrowUp className="me-1" size={10} />
                  {leadStats.totalChange} from last month
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(56, 178, 172, 0.1)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaUsers size={24} color="#38b2ac" />
              </div>
              <div>
                <div className="text-muted small mb-1">New Leads</div>
                <h3 className="mb-1 fs-4">{leadStats.newLeads}</h3>
                <div className="small text-success">
                  <FaArrowUp className="me-1" size={10} />
                  {leadStats.newChange} from last month
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(16, 185, 129, 0.1)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaCheckCircle size={24} color="#10b981" />
              </div>
              <div>
                <div className="text-muted small mb-1">Processed Leads</div>
                <h3 className="mb-1 fs-4">{leadStats.processedLeads}</h3>
                <div className="small text-success">
                  <FaArrowUp className="me-1" size={10} />
                  {leadStats.processedChange} from last month
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex">
              <div className="d-flex align-items-center justify-content-center me-3" style={{ background: 'rgba(239, 68, 68, 0.1)', width: '48px', height: '48px', borderRadius: '8px' }}>
                <FaExclamationCircle size={24} color="#ef4444" />
              </div>
              <div>
                <div className="text-muted small mb-1">Completed Leads</div>
                <h3 className="mb-1 fs-4">{leadStats.completedLeads}</h3>
                <div className="small text-danger">
                  <FaArrowDown className="me-1" size={10} />
                  {leadStats.completedChange} from last month
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Lead Processing Trends</h5>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Bar options={chartOptions} data={chartData} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={4}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <h5 className="mb-0">Lead Completion Status</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">Personal Loans</span>
                  <span className="small fw-bold">68%</span>
                </div>
                <ProgressBar now={68} variant="primary" style={{ height: '8px' }} />
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">Credit Cards</span>
                  <span className="small fw-bold">82%</span>
                </div>
                <ProgressBar now={82} variant="success" style={{ height: '8px' }} />
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">Home Loans</span>
                  <span className="small fw-bold">45%</span>
                </div>
                <ProgressBar now={45} variant="warning" style={{ height: '8px' }} />
              </div>
              
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">Car Loans</span>
                  <span className="small fw-bold">72%</span>
                </div>
                <ProgressBar now={72} variant="info" style={{ height: '8px' }} />
              </div>
              
              <div className="mb-0">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">Business Loans</span>
                  <span className="small fw-bold">36%</span>
                </div>
                <ProgressBar now={36} variant="danger" style={{ height: '8px' }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recent Leads</h5>
              <Button variant="outline-primary" size="sm" onClick={() => {}}>View All</Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Profile Completion</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map(lead => (
                    <tr key={lead.id}>
                      <td>{lead.name}</td>
                      <td>{lead.product}</td>
                      <td>{renderStatusBadge(lead.status)}</td>
                      <td>{lead.date}</td>
                      <td style={{ width: '20%' }}>{renderProgressBar(lead.completion)}</td>
                      <td>
                        <Button variant="outline-primary" size="sm">Process</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default BackofficeHome;